import { ProjectRenderer } from '../ProjectRenderer';
import { PlaygroundVertexList } from '../utils/PlaygroundVertexList';
import {
	PlaygroundVertex,
	SerializedPlaygroundVertex,
} from '../objects/PlaygroundVertex';
import {
	CameraType,
	PlaygroundShape,
	PlaygroundShapeType,
} from '@/types/creator';
import { PlaygroundLine } from '../objects/PlaygroundLine';
import {
	PlaygroundFloor,
	SerializedPlaygroundFloor,
} from '../objects/PlaygroundFloor';
import { Vector2, Vector2Tuple } from 'three';
import { RendererMode } from '../types';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export type SerializedPlaygroundManager = {
	shapeType: PlaygroundShapeType;
	vertices: SerializedPlaygroundVertex[];
	floor: SerializedPlaygroundFloor;
};

export class PlaygroundManager {
	lines: PlaygroundLine[] = [];
	shapeType = PlaygroundShapeType.RECTANGLE;
	vertexList = new PlaygroundVertexList();
	floor: PlaygroundFloor;

	constructor(private renderer: ProjectRenderer) {
		this.floor = new PlaygroundFloor(this.renderer, this);
	}

	private initCustomShapeMode() {
		this.renderer.mode = RendererMode.CUSTOM_SHAPE_DRAWING;

		if (this.renderer.isTouchDevice) return;

		const movableVertex = new PlaygroundVertex(this.renderer, this, [0, 0]);
		movableVertex.isMovable = true;
		this.vertexList.add(movableVertex);
	}

	private addLine(vert1: PlaygroundVertex, vert2: PlaygroundVertex) {
		const playgroundLine = new PlaygroundLine(
			this.renderer,
			this,
			vert1,
			vert2
		);
		this.lines.push(playgroundLine);
	}

	private createLines() {
		this.vertexList.forEach((vertex) => {
			if (!vertex.next) return;

			this.addLine(vertex, vertex.next);
		});
	}

	private setLineLabelsAvailability(force = false) {
		const visible =
				force ||
				([
					RendererMode.SHAPES,
					RendererMode.CUSTOM_SHAPE_DRAWING,
					RendererMode.PREVIEW,
				].includes(this.renderer.mode) &&
					this.renderer.cameraController.activeCamera ===
						CameraType.ORTHOGRAPHIC),
			isReadonly = [
				RendererMode.CUSTOM_SHAPE_DRAWING,
				RendererMode.PREVIEW,
			].includes(this.renderer.mode);

		for (const line of this.lines) {
			line.css2dObject.visible = visible;
			line.input.readOnly = isReadonly;
		}

		this.floor.input.readOnly = isReadonly;
		this.floor.css2dObject.visible =
			visible && this.shapeType === PlaygroundShapeType.CIRCLE;
	}

	private getLine(vert1: PlaygroundVertex, vert2: PlaygroundVertex) {
		const match = this.lines.find((line) => {
			return (
				(line.start === vert1 && line.end === vert2) ||
				(line.start === vert2 && line.end === vert1)
			);
		});

		if (!match) throw new Error('LINE_NOT_FOUND');
		return match;
	}

	private detectNearClosing() {
		if (this.vertexList.length < 3) return;
		if (!this.vertexList.last || !this.vertexList.first) return;

		if (
			this.vertexList.last.position.distanceTo(this.vertexList.first.position) >
			RENDERER_CONFIG.VERTEX.CLOSE_THRESHOLD
		)
			return;

		this.vertexList.last.position = this.vertexList.first.position.clone();
	}

	private tryCloseShapeMouse() {
		if (this.vertexList.length < 4) return false;
		if (!this.vertexList.last || !this.vertexList.first) return false;

		if (
			this.vertexList.last.position.distanceTo(
				this.vertexList.first.position
			) !== 0
		)
			return false;

		return true;
	}

	private tryCloseShapeTouch(pos: Vector2Tuple) {
		if (this.vertexList.length < 3) return false;
		if (!this.vertexList.last || !this.vertexList.first) return false;

		return (
			new Vector2(...pos).distanceTo(this.vertexList.first.position) <
			RENDERER_CONFIG.VERTEX.CLOSE_THRESHOLD
		);
	}

	resetCustomShape() {
		this.dispose();
		this.initCustomShapeMode();
	}

	setFromShape(shape: PlaygroundShape) {
		this.dispose();

		this.shapeType = shape.type;

		switch (shape.type) {
			case PlaygroundShapeType.RECTANGLE:
				this.renderer.mode = RendererMode.SHAPES;
				for (const position of shape.vertices) {
					const playgroundVertex = new PlaygroundVertex(
						this.renderer,
						this,
						position
					);
					this.vertexList.add(playgroundVertex);
				}

				this.vertexList.closeLoop();
				this.createLines();
				this.floor.createFloor();
				break;
			case PlaygroundShapeType.CIRCLE:
				this.renderer.mode = RendererMode.SHAPES;
				this.vertexList.isClosed = true;
				this.floor.radius = shape.radius;
				break;
			case PlaygroundShapeType.CUSTOM: {
				this.initCustomShapeMode();

				break;
			}
			default:
				throw new Error('INVALID_SHAPE_TYPE');
		}
	}

	dispose() {
		this.floor.dispose();

		this.vertexList.clear();

		for (const line of this.lines) {
			line.dispose();
		}

		this.lines = [];
	}

	setAvailability() {
		this.setLineLabelsAvailability();

		this.vertexList.forEach((vertex) => {
			vertex.setObjectVisibility();
		});
	}

	prepareScreenshot(isFinished = false) {
		this.setLineLabelsAvailability(!isFinished);
	}

	handlePointerMove(pos: Vector2Tuple) {
		const vertex = this.vertexList.last;
		if (!vertex) return;

		vertex.position = new Vector2(...pos);

		if (!vertex.prev) return;
		this.detectNearClosing();

		const line = this.getLine(vertex.prev, vertex);
		if (!line) return;

		line.checkIntersectionsWithOthers(this.lines);
	}

	handlePointerDown(pos: Vector2Tuple) {
		const lastVertex = this.vertexList.last;

		if (this.renderer.isTouchDevice) {
			if (this.tryCloseShapeTouch(pos)) {
				this.renderer.mode = RendererMode.SHAPES;
				this.addLine(lastVertex!, this.vertexList.first!);
				this.vertexList.closeLoop();
				this.floor.createFloor();
				return;
			}

			const newVert = new PlaygroundVertex(this.renderer, this, pos);
			this.vertexList.add(newVert);
			if (lastVertex) this.addLine(lastVertex, newVert);

			return;
		}
		if (!lastVertex) return;
		if (lastVertex.isError) return;

		lastVertex.isMovable = false;

		if (this.tryCloseShapeMouse()) {
			const oldLine = this.getLine(lastVertex.prev!, lastVertex);
			if (!oldLine) return;

			oldLine.updateMaterial();

			this.vertexList.remove(lastVertex);

			this.renderer.mode = RendererMode.SHAPES;

			this.vertexList.closeLoop();
			oldLine.end = this.vertexList.first!;
			this.floor.createFloor();

			return;
		}

		const newVert = new PlaygroundVertex(this.renderer, this, pos);
		newVert.isMovable = true;
		this.vertexList.add(newVert);

		this.addLine(lastVertex, newVert);

		if (!lastVertex.prev) return;

		const oldLine = this.getLine(lastVertex.prev!, lastVertex);
		if (!oldLine) return;

		oldLine.updateMaterial();
	}

	export(): SerializedPlaygroundManager {
		return {
			shapeType: this.shapeType,
			vertices: this.vertexList.toArray().map((v) => v.export()),
			floor: this.floor.export(),
		};
	}

	loadFromData(data: SerializedPlaygroundManager) {
		this.dispose();

		this.shapeType = data.shapeType;
		switch (this.shapeType) {
			case PlaygroundShapeType.RECTANGLE:
			case PlaygroundShapeType.CUSTOM:
				for (const config of data.vertices) {
					const playGroundVertex = new PlaygroundVertex(
						this.renderer,
						this,
						config.position
					);

					this.vertexList.add(playGroundVertex);
				}

				this.vertexList.closeLoop();
				this.createLines();

				this.floor.createFloor();
				if (data.floor.asset) this.floor.setCustomMaterial(data.floor.asset);
				break;
			case PlaygroundShapeType.CIRCLE:
				this.floor.radius = data.floor.radius;
				break;

			default:
				throw new Error('INVALID_SHAPE_TYPE');
		}

		this.setAvailability();
	}
}
